import { createContext, useContext, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../../api";
import { useAuth } from "../users";

export const MoviesContext = createContext({});

function useMovies() {
  const context = useContext(MoviesContext);

  if (!context) {
    throw new Error("useMovies must be used within a MoviesProvider");
  }

  return context;
}

const MoviesProvider = ({ children }) => {
  const [updater, setUpdater] = useState(true);
  const [movies, setMovies] = useState([]);
  const [movie, setMovie] = useState({});
  const { accessToken } = useAuth();

  const getMovies = async () => {
    await api
      .get("/movies", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((res) => {
        setMovies(res.data);
        setUpdater(!updater);
      })
      .catch((err) => {
        toast.error(err.response.data.error);
      });
  };

  const getMovieById = async (movieId) => {
    await api
      .get(`/movies/${movieId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((res) => {
        setMovie(res.data);
      })
      .catch((err) => {
        toast.error(err.response.data.error);
      });
  };

  const createMovie = async (movie) => {
    await api
      .post("/movies/register", movie, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((res) => {
        toast.success("Filme registrado com sucesso!");
      })
      .catch((err) => {
        toast.error(err.response.data.error);
      })
      .finally(() => getMovies());
  };

  const deleteMovie = async (movieId) => {
    await api
      .delete(`/movies/${movieId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((res) => {
        toast.success("Filme deletado com sucesso!");
      })
      .catch((err) => {
        toast.error(err.response.data.error);
      })
      .finally(() => getMovies());
  };

  const editMovie = async (movieId, payload) => {
    await api
      .patch(`/movies/${movieId}`, payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((res) => {
        toast.success("Filme editado com sucesso!");
      })
      .catch((err) => {
        toast.error(err.response.data.error);
      })
      .finally(() => getMovieById(movieId), getMovies());
  };

  const rateMovie = async (type, ratingId, movieId) => {
    await api
      .patch(`/movies/rate/${type}/${ratingId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then()
      .catch((err) => {
        toast.error(err.response.data.error);
      })
      .finally(() => getMovieById(movieId), getMovies());
  };

  const addComment = async (movieId, comment) => {
    await api
      .patch(`/movies/${movieId}/comment`, comment, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then(() => {
        toast.success("Comentário adicionado com sucesso!");
      })
      .catch((err) => {
        toast.error(err.response.data.error);
      })
      .finally(() => getMovieById(movieId), getMovies());
  };

  const deleteComment = async (commentId, movieId) => {
    await api
      .delete(`/movies/comment/${commentId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then(() => {
        toast.success("Comentário deletado com sucesso!");
      })
      .catch((err) => {
        toast.error(err.response.data.error);
      })
      .finally(() => getMovies(), getMovieById(movieId));
  };

  return (
    <MoviesContext.Provider
      value={{
        updater,
        setUpdater,
        movies,
        movie,
        setMovie,
        getMovies,
        getMovieById,
        createMovie,
        deleteMovie,
        editMovie,
        rateMovie,
        addComment,
        deleteComment,
      }}
    >
      {children}
    </MoviesContext.Provider>
  );
};

export { MoviesProvider, useMovies };
